/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentProps } from 'preact';
import { useCallback } from 'preact/hooks';

import { useFormContext } from '../hooks/UNSAFE_useFormContext';
import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { useCheckboxSetContext } from '../UNSAFE_CheckboxSet';

import { CheckboxControl } from '../PRIVATE_CheckboxControl/CheckboxControl';
import { InlineUserAssistance } from '#UNSAFE_UserAssistance';

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

type Props = {
  /**
   * Text that describes the checkbox item value.
   */
  children: string;
  /**
   * Text to provide guidance to help the user understand what data to enter.
   */
  assistiveText?: InlineUserAssistanceProps['assistiveText'];
  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];
  /**
   * Custom text to be rendered for the `helpSourceLink`.
   */
  helpSourceText?: InlineUserAssistanceProps['helpSourceText'];
  /**
   * Value of the checkbox item.
   */
  value: string | number;
};

/**
 * CheckboxItem is a child component of CheckboxSet. It contains the checkbox, label, and assistive text.
 */
const CheckboxItem = ({
  children,
  value,
  assistiveText,
  helpSourceLink,
  helpSourceText
}: Props) => {
  const { isReadonly: isFormReadonly, isDisabled: isFormDisabled } = useFormContext();
  const { isDisabled: isFieldDisabled, isReadonly: isFieldReadonly } = useFormFieldContext();
  const { name, onCommit, value: groupValue } = useCheckboxSetContext();

  const isChecked = groupValue?.has(value);
  const isDisabled = isFieldDisabled ?? isFormDisabled;
  const isReadonly = isFieldReadonly ?? isFormReadonly;

  const onChangeHandler = useCallback(
    (e: Event) => {
      // return early on keyup if key isn't spacebar
      const { key } = e as KeyboardEvent;
      if (key !== undefined && key !== ' ') {
        return;
      }

      // create a new set from the previous and add or remove the changed value
      const newGroupValue = new Set(groupValue);
      if (newGroupValue.has(value)) {
        newGroupValue.delete(value);
      } else {
        newGroupValue.add(value);
      }

      onCommit?.({
        previousValue: groupValue,
        value: newGroupValue.size > 0 ? newGroupValue : undefined
      });
    },
    [groupValue, onCommit, value]
  );

  return (
    <CheckboxControl
      name={name}
      value={value}
      {...(!isReadonly && !isDisabled
        ? { onChange: onChangeHandler, onKeyUp: onChangeHandler }
        : {})}
      assistiveText={assistiveText}
      helpSourceLink={helpSourceLink}
      helpSourceText={helpSourceText}
      isChecked={isChecked}
      isDisabled={isDisabled}
      isReadonly={isReadonly}>
      {children}
    </CheckboxControl>
  );
};

export { CheckboxItem };
